import debug from 'debug'
import { Map as MapLibreMap } from 'maplibre-gl'
import 'maplibre-gl/src/css/maplibre-gl.css'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { changeDocumentLocation } from '../../store/history/history.utils'
import { selectLocale } from '../../store/locale/locale.selectors'
import { mapDestroyed, mapResetStyle, setMapStatus, toggleFullscreen, update } from '../../store/map/map.actions'
import {
  MAPLIBRE_EVENT_MOVE,
  MAPLIBRE_EVENT_ZOOM,
  MAP_STATUS_INIT,
  MAP_STATUS_STYLED
} from '../../store/map/map.constants'
import {
  selectCouldMapFullScreen,
  selectIsMapFullScreen,
  selectMapBbox,
  selectMapMaxZoom,
  selectMapService,
  selectMapStatus
} from '../../store/map/map.selectors'
import { debounce, throttle } from '../../utils/function'
import { isGE } from '../Device'
import { getMapSessionId } from '../analytics/BiTagger'
import MapLoadingError from '../error/MapLoadingError'
import SentryService from '../error/SentryService'
import { isPolylineAroundPointer } from '../utils/map'
import { useAction } from '../utils/react-redux'
import { useRefFromSelector } from '../utils/selector'
import { addParamsToUrl } from '../utils/url'
import useErrorBoundaryCallback from '../utils/useErrorBoundaryCallback'
import s from './DynamicMap.less'
import { MapContext } from './MapContext'
import { MAP_STORE_UPDATE_DELAY, MAP_ZOOM_MIN } from './map.constants'
import {
  MAP_FROM_APP_ACTION_MOVE,
  MAP_FROM_APP_ACTION_PADDING,
  computeMapActiveBbox,
  computePixelsDeltaBetweenBboxes,
  computeSearchBbox,
  isWebglSupported
} from './map.utils'
import { handleMobileLongTouchAsContextMenu, handleMobileTap } from './maplibre.fixes'

const d = debug('map')

const redirectToWebGLErrorPage = error => {
  const mapLoadingError = new MapLoadingError(error)
  SentryService.captureException(mapLoadingError)
  changeDocumentLocation('/erreur-support-webgl')
}

// webpack:BrowserOnly
const MapProvider = ({ children }) => {
  // state
  const mapInstanceRef = useRef(null)
  const mapContainerRef = useRef()
  const [mapStyleName, setMapStyleName] = useState('')

  // selectors
  const storeBboxRef = useRefFromSelector(selectMapBbox)
  const couldFullScreen = useSelector(selectCouldMapFullScreen)
  const isMapFullScreen = useSelector(selectIsMapFullScreen)
  const mapService = useSelector(selectMapService)
  const maxZoom = useSelector(selectMapMaxZoom)
  const mapStatus = useSelector(selectMapStatus)
  const locale = useSelector(selectLocale)

  // actions
  const execUpdate = useAction(update)
  const execSetMapStatus = useAction(setMapStatus)
  const execMapDestroyed = useAction(mapDestroyed)
  const execResetStyle = useAction(mapResetStyle)
  const execToggleFullscreen = useAction(toggleFullscreen)

  const getMapStartLocation = useCallback(() => {
    if (global.GEOIP) {
      const { lat, lng } = global.GEOIP
      delete global.GEOIP // Only at first page load
      const startLocation = {
        center: [lng, lat],
        zoom: 11
      }
      if (d.enabled) d('getMapStartLocation - GEOIP:', JSON.stringify(startLocation))
      return startLocation
    }
    const startLocation = {
      bounds: storeBboxRef.current
    }
    if (d.enabled) d('getMapStartLocation:', JSON.stringify(startLocation))
    return startLocation
  }, [])

  const getMapLibreglState = useCallback(
    () => ({
      bbox: mapInstanceRef.current.getBounds().toArray(),
      zoom: mapInstanceRef.current.getZoom()
    }),
    []
  )

  const onUpdate = useCallback((event = {}) => {
    const { fromAppAction } = event
    const { bbox, zoom } = getMapLibreglState()
    const activeBbox = computeMapActiveBbox(mapInstanceRef.current)
    const searchBbox = computeSearchBbox(mapInstanceRef.current)
    const distanceFromPreviousChange = computePixelsDeltaBetweenBboxes(
      mapInstanceRef.current,
      bbox,
      storeBboxRef.current
    )

    if (d.enabled) {
      d('onUpdate, distanceFromPreviousChange:', distanceFromPreviousChange, 'bbox:', bbox)
    }

    execUpdate({
      bbox,
      zoom,
      activeBbox,
      searchBbox,
      fromAppAction,
      distanceFromPreviousChange
    })
  }, [])

  const getUpdateHandler = () => {
    const delayedUpdate = debounce(onUpdate, MAP_STORE_UPDATE_DELAY)
    return (event = {}) => {
      const { fromAppAction } = event
      if (fromAppAction === MAP_FROM_APP_ACTION_PADDING) {
        onUpdate(event)
      } else {
        delayedUpdate(event)
      }
    }
  }

  /*
  This useEffect must be executed before the following one,
   and has no dependency with mapInstanceRef to not be re-executed when map is initialized
   */
  useEffect(() => {
    if (!mapInstanceRef.current) return
    execResetStyle()
    mapInstanceRef.current.setStyle(mapService.url)
  }, [mapService.url])

  const updateMapStatusOnRender = useCallback(() => {
    if (mapStatus === MAP_STATUS_STYLED) return
    if (mapInstanceRef.current.isStyleLoaded()) {
      const { name } = mapInstanceRef.current.getStyle()
      if (mapStyleName !== name) {
        setMapStyleName(name)
        execSetMapStatus(MAP_STATUS_STYLED)
      }
    }
  }, [mapStatus, mapStyleName])

  useEffect(() => {
    if (mapStatus === MAP_STATUS_INIT) {
      setMapStyleName('')
    }
  }, [mapStatus])

  useEffect(() => {
    if (!mapInstanceRef.current) return
    mapInstanceRef.current.resize()
  }, [isMapFullScreen])

  useEffect(() => {
    if (!isWebglSupported()) {
      redirectToWebGLErrorPage('no-webgl-support')
      return
    }

    const { url: style, apikey } = mapService
    const sessionId = getMapSessionId()

    try {
      mapInstanceRef.current = new MapLibreMap({
        container: mapContainerRef.current,
        style,
        pitchWithRotate: false,
        dragRotate: false,
        maxZoom,
        minZoom: MAP_ZOOM_MIN,
        doubleClickZoom: isGE(), // NES-1427: Differentiate single and double click « manually » on PE, see handleMobileTap method
        keyboard: true,
        attributionControl: false,
        ...getMapStartLocation(),
        transformRequest: url => {
          const params = { sessionId }
          if (apikey) params.apikey = apikey
          return { url: addParamsToUrl(url, params), headers: { 'accept-language': locale } }
        }
      })
      handleMobileLongTouchAsContextMenu(mapInstanceRef.current)

      mapInstanceRef.current.touchZoomRotate.disableRotation()

      execSetMapStatus(MAP_STATUS_INIT)

      const updateHandler = getUpdateHandler()
      mapInstanceRef.current.on(MAPLIBRE_EVENT_MOVE, updateHandler)
      mapInstanceRef.current.on(MAPLIBRE_EVENT_ZOOM, updateHandler)

      mapInstanceRef.current.once('render', () => {
        const { bbox, zoom } = getMapLibreglState()
        const activeBbox = computeMapActiveBbox(mapInstanceRef.current)
        const searchBbox = computeSearchBbox(mapInstanceRef.current)

        if (d.enabled) {
          d('update on first render', bbox)
        }

        execUpdate({
          bbox,
          zoom,
          activeBbox,
          searchBbox,
          fromAppAction: MAP_FROM_APP_ACTION_MOVE
        }) // on first render, we want to update bbox in store (which may be France if geoip was used)
      })

      return () => {
        mapInstanceRef.current.off('render', updateMapStatusOnRender)
        mapInstanceRef.current.remove()
        execMapDestroyed()
      }
    } catch (e) {
      redirectToWebGLErrorPage(e)
    }
  }, [])

  useEffect(() => {
    const throttledUpdateMapStyle = throttle(updateMapStatusOnRender, 100)
    if (mapInstanceRef.current) {
      mapInstanceRef.current.on('render', throttledUpdateMapStyle)
      return () => {
        mapInstanceRef.current.off('render', throttledUpdateMapStyle)
      }
    }
  }, [updateMapStatusOnRender])

  const onMapTouchEvent = useErrorBoundaryCallback(
    e => {
      if (!e.handled && couldFullScreen && !isPolylineAroundPointer(mapInstanceRef.current, e)) {
        e.handled = true
        execToggleFullscreen()
      }
    },
    [couldFullScreen, isPolylineAroundPointer]
  )

  const mobileTapHandler = useErrorBoundaryCallback(
    e => {
      handleMobileTap(e, mapInstanceRef.current, onMapTouchEvent, { maxZoom })
    },
    [onMapTouchEvent]
  )

  useEffect(() => {
    if (!mapInstanceRef.current || isGE()) return

    mapInstanceRef.current.on('click', mobileTapHandler)
    return () => {
      mapInstanceRef.current.off('click', mobileTapHandler)
    }
  }, [mobileTapHandler, mapInstanceRef.current])

  return (
    <MapContext.Provider value={{ map: mapInstanceRef.current }}>
      <div className={s.DynamicMap} ref={mapContainerRef}>
        {children}
      </div>
    </MapContext.Provider>
  )
}

export default MapProvider
